import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { analyticsAPI, commentsAPI } from "../services/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, LineChart, Line,
} from "recharts";
import { TrendingUp, TrendingDown, Home, BookOpen, Eye, Download, X, CalendarClock, Star } from "lucide-react";

// ── Palette for room bars ─────────────────────────────────────────────────────
const COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#3b82f6","#ec4899","#14b8a6","#8b5cf6"];

function formatCurrency(n: number): string {
  if (!n) return "0đ";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "tr";
  return new Intl.NumberFormat("vi-VN").format(n) + "đ";
}

function formatCurrencyFull(n: number): string {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(n || 0);
}

interface TooltipPayloadItem {
  dataKey: string;
  value: number;
  color: string;
}

interface RevenueTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

// Custom tooltip for chart — only show rooms with revenue > 0
function RevenueTooltip({ active, payload, label }: RevenueTooltipProps): React.ReactElement | null {
  if (!active || !payload?.length) return null;
  const active_items = payload.filter((p) => p.value > 0);
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  if (total === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm min-w-[160px]">
      <p className="font-bold text-gray-800 mb-2">{label}</p>
      {active_items.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.dataKey}</span>
          <span className="font-medium">{formatCurrency(p.value)}</span>
        </div>
      ))}
      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between font-bold">
        <span className="text-gray-600">Tổng</span>
        <span className="text-gray-900">{formatCurrency(total)}</span>
      </div>
    </div>
  );
}

interface CustomerStat {
  _id: string;
  name: string;
  email: string;
  phone: string;
  gender: string;
  dateOfBirth: string;
  hasCmnd: boolean;
  bookingsThisMonth: number;
  bookingsThisYear: number;
  bookingsAllTime: number;
}

interface OverviewData {
  totalViews: number;
  totalBookings: number;
  totalRevenue: number;
  totalRevenueLLD: number;
  totalRooms: number;
  currentMonthRevenue: number;
  previousMonthRevenue: number;
}

interface UpcomingBooking {
  _id: string;
  roomName: string;
  guestName?: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  status: string;
  source?: string;
}

interface RecentReview {
  _id: string;
  roomName: string;
  userName: string;
  rating: number;
  comment: string;
  status: string;
  createdAt: string;
}

interface RoomStat {
  _id: string;
  name: string;
  totalViews: number;
  recentViews: number;
  bookings: number;
  revenue: number;
  lldRevenue: number;
}

interface RoomTotal {
  _id: string;
  name: string;
  totalRevenue: number;
  totalBookings: number;
}

interface RevenueData {
  chartData: Record<string, unknown>[];
  roomNames: string[];
  roomTotals: RoomTotal[];
}

interface RoomCleaningStat {
  _id: string;
  name: string;
  district: string;
  website: number;
  lld: number;
  total: number;
}

export default function DashboardPage(): React.ReactElement {
  const { user } = useAuth();
  const isRoommaster = user?.role === "roommaster";

  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [roomStats, setRoomStats] = useState<RoomStat[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [cleaningShifts, setCleaningShifts] = useState<RoomCleaningStat[]>([]);
  const [upcomingBookings, setUpcomingBookings] = useState<UpcomingBooking[]>([]);
  const [recentReviews, setRecentReviews] = useState<RecentReview[]>([]);
  const [months, setMonths] = useState<number>(6);
  const [loading, setLoading] = useState<boolean>(true);
  const [revenueLoading, setRevenueLoading] = useState<boolean>(false);
  const [customerStats, setCustomerStats] = useState<CustomerStat[]>([]);
  const [customerMonth, setCustomerMonth] = useState<string>("");
  const [customerYear, setCustomerYear] = useState<string>("");
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const [viewDetailRoom, setViewDetailRoom] = useState<RoomStat | null>(null);
  const [viewDetail, setViewDetail] = useState<{ daily: { date: string; count: number }[]; today: number; total: number } | null>(null);
  const [viewDetailLoading, setViewDetailLoading] = useState(false);

  const handleOpenViewDetail = async (room: RoomStat) => {
    setViewDetailRoom(room);
    setViewDetail(null);
    setViewDetailLoading(true);
    try {
      const res = await analyticsAPI.getRoomViewDetail(room._id, 30);
      setViewDetail(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setViewDetailLoading(false);
    }
  };

  const [overviewMonth, setOverviewMonth] = useState<string>("");

  // Load customer stats + upcoming + reviews once (không phụ thuộc tháng)
  useEffect(() => {
    (async () => {
      try {
        if (!isRoommaster) {
          const custRes = await analyticsAPI.getCustomerStats();
          setCustomerStats(custRes.data.data || []);
          setCustomerMonth(custRes.data.month || "");
          setCustomerYear(custRes.data.year || "");
        }

        const upcomingRes = await analyticsAPI.getUpcomingBookings(8);
        setUpcomingBookings(upcomingRes.data.data || []);

        const reviewRes = await commentsAPI.getAll();
        const approved = ((reviewRes.data.data || []) as RecentReview[])
          .filter((r) => r.status === "approved")
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, 5);
        setRecentReviews(approved);
      } catch (e) {
        console.error("Dashboard load error:", e);
      }
    })();
  }, [isRoommaster]);

  // Load doanh thu tổng quan mỗi khi đổi bộ lọc tháng (kể cả lần đầu)
  useEffect(() => {
    (async () => {
      try {
        const res = await analyticsAPI.getOverview(overviewMonth || undefined);
        setOverview(res.data.data);
      } catch (e) {
        console.error("Overview load error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [overviewMonth]);

  // Load số ca vệ sinh theo phòng, cùng bộ lọc tháng với doanh thu
  useEffect(() => {
    (async () => {
      try {
        const res = await analyticsAPI.getCleaningShifts(overviewMonth || undefined);
        setCleaningShifts(res.data.data || []);
      } catch (e) {
        console.error("Cleaning shifts load error:", e);
      }
    })();
  }, [overviewMonth]);

  // Load doanh thu theo phòng, cùng bộ lọc tháng với doanh thu tổng quan
  useEffect(() => {
    (async () => {
      try {
        const res = await analyticsAPI.getRoomStats(overviewMonth || undefined);
        setRoomStats(res.data.data || []);
      } catch (e) {
        console.error("Room stats load error:", e);
      }
    })();
  }, [overviewMonth]);

  // Load revenue chart whenever months changes
  const loadRevenue = useCallback(async (m: number) => {
    setRevenueLoading(true);
    try {
      const res = await analyticsAPI.getMonthlyRevenue(m);
      setRevenueData(res.data.data);
    } catch (e) {
      console.error("Revenue load error:", e);
    } finally {
      setRevenueLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRevenue(months);
  }, [months, loadRevenue]);

  const cleaningByDistrict = useMemo(() => {
    const groups = new Map<string, RoomCleaningStat[]>();
    cleaningShifts.forEach((r) => {
      if (!groups.has(r.district)) groups.set(r.district, []);
      groups.get(r.district)!.push(r);
    });
    return Array.from(groups.entries()).map(([district, rooms]) => ({
      district,
      rooms,
      website: rooms.reduce((s, r) => s + r.website, 0),
      lld: rooms.reduce((s, r) => s + r.lld, 0),
      total: rooms.reduce((s, r) => s + r.total, 0),
    }));
  }, [cleaningShifts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coffee-600" />
      </div>
    );
  }

  const totalViews      = overview?.totalViews      || 0;
  const totalBookings   = overview?.totalBookings   || 0;
  const totalRevenue    = overview?.totalRevenue    || 0;
  const totalRevenueLLD = overview?.totalRevenueLLD || 0;
  const totalRooms      = overview?.totalRooms      || 0;
  const curMonthRevenue  = overview?.currentMonthRevenue  || 0;
  const prevMonthRevenue = overview?.previousMonthRevenue || 0;
  const monthChangePct = prevMonthRevenue > 0
    ? Math.round(((curMonthRevenue - prevMonthRevenue) / prevMonthRevenue) * 100)
    : (curMonthRevenue > 0 ? 100 : 0);

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
          Xin chào{user?.name ? `, ${user.name}` : ""} 👋
        </h1>
        <p className="text-sm text-zinc-500 mt-0.5">
          {isRoommaster ? "Tổng quan phòng của bạn" : "Tổng quan hoạt động kinh doanh"}
        </p>
      </div>

      {/* ── Revenue month filter ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="overview-month" className="text-xs font-medium text-zinc-500">Xem doanh thu tháng:</label>
        <input
          id="overview-month"
          type="month"
          value={overviewMonth}
          onChange={(e) => setOverviewMonth(e.target.value)}
          className="px-2.5 py-1.5 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-coffee-300"
        />
        <span className="text-xs text-zinc-400">{overviewMonth ? "" : "Đang xem tất cả thời gian"}</span>
        {overviewMonth && (
          <button
            onClick={() => setOverviewMonth("")}
            className="text-xs text-coffee-600 hover:underline font-medium"
          >
            Xem tất cả
          </button>
        )}
        {(curMonthRevenue > 0 || prevMonthRevenue > 0) && (
          <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${
            monthChangePct >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
          }`}>
            {monthChangePct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            Tháng này {monthChangePct >= 0 ? "+" : ""}{monthChangePct}% so với tháng trước
          </span>
        )}
      </div>

      {/* ── Revenue cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 transition-colors">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest mb-2">Doanh thu LLD</p>
              <p className="text-2xl font-bold text-zinc-900 tracking-tight">{formatCurrencyFull(totalRevenueLLD)}</p>
              <p className="text-xs text-zinc-400 mt-1.5">Từ Google Sheets {overviewMonth ? `· T${overviewMonth.split("-")[1]}/${overviewMonth.split("-")[0]}` : "· Tất cả thời gian"}</p>
            </div>
            <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-100">
              <TrendingUp size={18} className="text-amber-600" />
            </div>
          </div>
          <div className="mt-4 h-1 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-400 rounded-full" style={{ width: totalRevenueLLD + totalRevenue > 0 ? `${(totalRevenueLLD / (totalRevenueLLD + totalRevenue)) * 100}%` : "0%" }} />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 transition-colors">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest mb-2">Doanh thu 23Homestay</p>
              <p className="text-2xl font-bold text-zinc-900 tracking-tight">{formatCurrencyFull(totalRevenue)}</p>
              <p className="text-xs text-zinc-400 mt-1.5">Từ website {overviewMonth ? `· T${overviewMonth.split("-")[1]}/${overviewMonth.split("-")[0]}` : "· Tất cả thời gian"}</p>
            </div>
            <div className="p-2.5 bg-coffee-50 rounded-lg border border-coffee-100">
              <Home size={18} className="text-coffee-600" />
            </div>
          </div>
          <div className="mt-4 h-1 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full bg-coffee-500 rounded-full" style={{ width: totalRevenueLLD + totalRevenue > 0 ? `${(totalRevenue / (totalRevenueLLD + totalRevenue)) * 100}%` : "0%" }} />
          </div>
        </div>
      </div>

      {/* ── Ca vệ sinh theo phòng (cùng bộ lọc tháng ở trên) ── */}
      {cleaningShifts.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h2 className="text-sm font-semibold text-zinc-900 mb-0.5">Ca vệ sinh theo phòng</h2>
          <p className="text-xs text-zinc-400 mb-4">
            Mỗi đơn đã xác nhận (Website + LLD) = 1 ca dọn phòng sau checkout {overviewMonth ? `· T${overviewMonth.split("-")[1]}/${overviewMonth.split("-")[0]}` : "· Tất cả thời gian"}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-3 pr-4 font-medium w-8">#</th>
                  <th className="pb-3 pr-4 font-medium">Phòng</th>
                  <th className="pb-3 pr-4 font-medium text-right">Ca Web</th>
                  <th className="pb-3 pr-4 font-medium text-right">Ca LLD</th>
                  <th className="pb-3 font-medium text-right">Tổng ca</th>
                </tr>
              </thead>
              <tbody>
                {cleaningByDistrict.map((group) => (
                  <React.Fragment key={group.district}>
                    <tr className="bg-zinc-50">
                      <td colSpan={5} className="py-2 pr-4 pl-2 font-bold text-zinc-700 text-xs uppercase tracking-wide">
                        {group.district}
                      </td>
                    </tr>
                    {group.rooms.map((r, i) => (
                      <tr key={r._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="py-3 pr-4 text-gray-400 font-medium">{i + 1}</td>
                        <td className="py-3 pr-4 font-semibold text-gray-900 pl-4">{r.name}</td>
                        <td className="py-3 pr-4 text-right text-sky-600 font-medium">{r.website || <span className="text-gray-300">—</span>}</td>
                        <td className="py-3 pr-4 text-right text-amber-600 font-medium">{r.lld || <span className="text-gray-300">—</span>}</td>
                        <td className="py-3 text-right font-bold text-gray-900">{r.total}</td>
                      </tr>
                    ))}
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      <td colSpan={2} className="py-2 pr-4 pl-4 text-xs font-semibold text-gray-500">Tổng {group.district}</td>
                      <td className="py-2 pr-4 text-right text-xs font-bold text-sky-600">{group.website}</td>
                      <td className="py-2 pr-4 text-right text-xs font-bold text-amber-600">{group.lld}</td>
                      <td className="py-2 text-right text-xs font-bold text-gray-800">{group.total}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan={2} className="py-3 pr-4 font-bold text-gray-700 pl-2">Tổng</td>
                  <td className="py-3 pr-4 text-right font-bold text-sky-600">{cleaningShifts.reduce((s, r) => s + r.website, 0)}</td>
                  <td className="py-3 pr-4 text-right font-bold text-amber-600">{cleaningShifts.reduce((s, r) => s + r.lld, 0)}</td>
                  <td className="py-3 text-right font-bold text-gray-900">{cleaningShifts.reduce((s, r) => s + r.total, 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest">Tổng booking</p>
            <div className="p-2 bg-blue-50 rounded-lg border border-blue-100"><BookOpen size={15} className="text-coffee-600" /></div>
          </div>
          <p className="text-2xl font-bold text-zinc-900 tracking-tight">{totalBookings.toLocaleString()}</p>
          <p className="text-xs text-zinc-400 mt-1">Pending + confirmed</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest">Số phòng</p>
            <div className="p-2 bg-coffee-50 rounded-lg border border-coffee-100"><Home size={15} className="text-coffee-600" /></div>
          </div>
          <p className="text-2xl font-bold text-zinc-900 tracking-tight">{totalRooms.toLocaleString()}</p>
          <p className="text-xs text-zinc-400 mt-1">Tổng số phòng</p>
        </div>
        <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest">Lượt xem</p>
            <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-100"><Eye size={15} className="text-emerald-600" /></div>
          </div>
          <p className="text-2xl font-bold text-zinc-900 tracking-tight">{totalViews.toLocaleString()}</p>
          <p className="text-xs text-zinc-400 mt-1">Tất cả thời gian</p>
        </div>
      </div>

      {/* ── Đơn sắp tới + Đánh giá gần đây ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={16} className="text-coffee-600" />
            <h2 className="text-sm font-semibold text-zinc-900">Đơn sắp tới</h2>
          </div>
          {upcomingBookings.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6">Chưa có đơn sắp check-in</p>
          ) : (
            <div className="space-y-2.5 max-h-80 overflow-y-auto">
              {upcomingBookings.map((b) => (
                <div key={b._id} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-50 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-800 truncate">{b.roomName}</p>
                    <p className="text-xs text-zinc-400 truncate">{b.guestName || "—"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-semibold text-zinc-700">{b.checkInDate} · {b.checkInTime}</p>
                    <p className="text-[10px] text-zinc-400">{b.status === "pending" ? "Chờ duyệt" : "Đã xác nhận"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Star size={16} className="text-amber-500" />
            <h2 className="text-sm font-semibold text-zinc-900">Đánh giá gần đây</h2>
          </div>
          {recentReviews.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6">Chưa có đánh giá nào được duyệt</p>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {recentReviews.map((r) => (
                <div key={r._id} className="pb-3 border-b border-zinc-50 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-zinc-800">{r.roomName}</p>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star key={i} size={11} className={i < r.rating ? "fill-amber-400 text-amber-400" : "text-zinc-200"} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-zinc-400 mt-0.5">{r.userName}</p>
                  <p className="text-xs text-zinc-600 mt-1 line-clamp-2">{r.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Revenue by room breakdown ── */}
      {roomStats.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h2 className="text-sm font-semibold text-zinc-900 mb-0.5">Doanh thu theo phòng</h2>
          <p className="text-xs text-zinc-400 mb-4">Chi tiết doanh thu LLD và 23Homestay từng phòng</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-3 pr-4 font-medium w-8">#</th>
                  <th className="pb-3 pr-4 font-medium">Phòng</th>
                  <th className="pb-3 pr-4 font-medium text-right">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                      DT LLD
                    </span>
                  </th>
                  <th className="pb-3 pr-4 font-medium text-right">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                      DT 23Homestay
                    </span>
                  </th>
                  <th className="pb-3 font-medium text-right">Tổng cộng</th>
                </tr>
              </thead>
              <tbody>
                {[...roomStats]
                  .sort((a, b) => (b.lldRevenue + b.revenue) - (a.lldRevenue + a.revenue))
                  .map((r, i) => {
                    const total = r.lldRevenue + r.revenue;
                    const maxTotal = Math.max(...roomStats.map((x) => x.lldRevenue + x.revenue), 1);
                    const pct = Math.round((total / maxTotal) * 100);
                    return (
                      <tr key={r._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="py-3 pr-4 text-gray-400 font-medium">{i + 1}</td>
                        <td className="py-3 pr-4">
                          <p className="font-semibold text-gray-900">{r.name}</p>
                          <div className="mt-1 h-1.5 w-full max-w-[160px] bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                background: "linear-gradient(to right, #fb923c, #22c55e)",
                              }}
                            />
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-right">
                          {r.lldRevenue > 0 ? (
                            <span className="font-medium text-orange-600">{formatCurrencyFull(r.lldRevenue)}</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-right">
                          {r.revenue > 0 ? (
                            <span className="font-medium text-green-700">{formatCurrencyFull(r.revenue)}</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          <span className="font-bold text-gray-900">{formatCurrencyFull(total)}</span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan={2} className="py-3 pr-4 font-bold text-gray-700 pl-2">Tổng</td>
                  <td className="py-3 pr-4 text-right font-bold text-orange-600">
                    {formatCurrencyFull(roomStats.reduce((s, r) => s + r.lldRevenue, 0))}
                  </td>
                  <td className="py-3 pr-4 text-right font-bold text-green-700">
                    {formatCurrencyFull(roomStats.reduce((s, r) => s + r.revenue, 0))}
                  </td>
                  <td className="py-3 text-right font-bold text-gray-900">
                    {formatCurrencyFull(roomStats.reduce((s, r) => s + r.lldRevenue + r.revenue, 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Monthly revenue chart ── */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Doanh thu theo tháng</h2>
            <p className="text-xs text-zinc-400 mt-0.5">Booking đã xác nhận (23Homestay)</p>
          </div>
          <div className="flex gap-1 bg-zinc-100 rounded-lg p-1">
            {[3, 6, 12].map((m) => (
              <button
                key={m}
                onClick={() => setMonths(m)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  months === m ? "bg-white text-coffee-700 shadow-sm font-semibold" : "text-zinc-500 hover:text-zinc-700"
                }`}
              >
                {m} tháng
              </button>
            ))}
          </div>
        </div>

        {revenueLoading ? (
          <div className="flex items-center justify-center h-56">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coffee-600" />
          </div>
        ) : revenueData?.chartData?.length ? (
          <>
            {(() => {
              const colorMap = Object.fromEntries(
                revenueData.roomNames.map((name, i) => [name, COLORS[i % COLORS.length]])
              );
              return (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueData.chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12, fill: "#6b7280" }}
                        tickFormatter={(v: string) => {
                          const [y, m] = v.split("-");
                          return `${m}/${y.slice(2)}`;
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#6b7280" }}
                        tickFormatter={(v: number) => formatCurrency(v)}
                        width={70}
                      />
                      <Tooltip content={<RevenueTooltip />} />
                      <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                        formatter={(value: string) => <span className="text-gray-700">{value}</span>}
                      />
                      {revenueData.roomNames.map((name, i) => (
                        <Bar key={name} dataKey={name} stackId="a" fill={COLORS[i % COLORS.length]} radius={i === revenueData.roomNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Per-room totals */}
                  <div className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5">
                    {revenueData.roomTotals.filter((r) => r.totalRevenue > 0).map((r) => (
                      <div key={r._id} className="rounded-lg border border-zinc-100 bg-zinc-50 p-3">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: colorMap[r.name] }} />
                          <span className="text-xs text-zinc-500 truncate font-medium">{r.name}</span>
                        </div>
                        <p className="text-sm font-bold text-zinc-900">{formatCurrency(r.totalRevenue)}</p>
                        <p className="text-xs text-zinc-400 mt-0.5">{r.totalBookings} booking</p>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </>
        ) : (
          <div className="flex items-center justify-center h-56 text-zinc-400 text-sm">
            Chưa có dữ liệu doanh thu
          </div>
        )}
      </div>

      {/* ── Room view ranking ── */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5">
        <h2 className="text-sm font-semibold text-zinc-900 mb-4">Lượt xem theo phòng</h2>
        {roomStats.length === 0 ? (
          <p className="text-zinc-400 text-sm">Chưa có dữ liệu</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-zinc-100">
                  <th className="pb-2.5 pr-4 text-xs font-medium text-zinc-400 w-8">#</th>
                  <th className="pb-2.5 pr-4 text-xs font-medium text-zinc-400">Phòng</th>
                  <th className="pb-2.5 pr-4 text-xs font-medium text-zinc-400 text-right">30 ngày</th>
                  <th className="pb-2.5 pr-4 text-xs font-medium text-zinc-400 text-right">Tổng lượt xem</th>
                  <th className="pb-2.5 pr-4 text-xs font-medium text-zinc-400 text-right">Booking</th>
                  <th className="pb-2.5 text-xs font-medium text-zinc-400 text-right">Doanh thu</th>
                </tr>
              </thead>
              <tbody>
                {roomStats.map((r, i) => {
                  const maxViews = roomStats[0]?.totalViews || 1;
                  const pct = Math.round((r.totalViews / maxViews) * 100);
                  return (
                    <tr
                      key={r._id}
                      className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => handleOpenViewDetail(r)}
                    >
                      <td className="py-3 pr-4 text-zinc-400 text-xs font-medium">{i + 1}</td>
                      <td className="py-3 pr-4">
                        <p className="font-medium text-zinc-800 text-sm">{r.name}</p>
                        <div className="mt-1.5 h-1 w-full max-w-[140px] bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full bg-coffee-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-right text-zinc-600 text-xs">{r.recentViews.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right font-semibold text-coffee-600 text-sm">{r.totalViews.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right text-zinc-600 text-xs">{r.bookings.toLocaleString()}</td>
                      <td className="py-3 text-right text-emerald-700 font-medium text-sm">{formatCurrencyFull(r.revenue)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Customer stats table ── */}
      {!isRoommaster && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Thống kê khách hàng</h2>
              <p className="text-xs text-zinc-400 mt-0.5">
                Gộp booking đăng nhập + đặt theo số điện thoại
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Tìm tên, SĐT, email..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                className="px-3 py-1.5 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:border-coffee-400 focus:ring-1 focus:ring-coffee-200 w-52"
              />
              <button
                onClick={() => exportCustomerCSV(customerStats, customerMonth, customerYear)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-700 text-white rounded-lg text-xs font-medium transition-colors"
              >
                <Download size={13} /> Xuất CSV
              </button>
            </div>
          </div>
          {customerStats.length === 0 ? (
            <p className="text-gray-400 text-sm">Chưa có dữ liệu khách hàng</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-zinc-100">
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400 w-8">#</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400">Họ tên</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400">SĐT</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400">Giới tính</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400">Ngày sinh</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400 text-center">CCCD</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400 text-right">T.{customerMonth?.split("-")[1]}/{customerMonth?.split("-")[0]?.slice(2)}</th>
                    <th className="pb-2.5 pr-3 text-xs font-medium text-zinc-400 text-right">Năm {customerYear}</th>
                    <th className="pb-2.5 text-xs font-medium text-zinc-400 text-right">Tổng</th>
                  </tr>
                </thead>
                <tbody>
                  {customerStats
                    .filter(c => {
                      if (!customerSearch) return true;
                      const q = customerSearch.toLowerCase();
                      return c.name.toLowerCase().includes(q) || c.phone.includes(q) || c.email.toLowerCase().includes(q);
                    })
                    .map((c, i) => (
                      <tr key={c._id} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50 transition-colors">
                        <td className="py-3 pr-3 text-zinc-400 text-xs font-medium">{i + 1}</td>
                        <td className="py-3 pr-3">
                          <p className="font-medium text-zinc-800 text-sm">{c.name}</p>
                          <p className="text-xs text-zinc-400 truncate max-w-[150px]">{c.email}</p>
                        </td>
                        <td className="py-3 pr-3 text-zinc-600 text-xs">{c.phone || <span className="text-zinc-300">-</span>}</td>
                        <td className="py-3 pr-3 text-zinc-600 text-xs">
                          {c.gender === "male" ? "Nam" : c.gender === "female" ? "Nu" : c.gender === "other" ? "Khac" : <span className="text-zinc-300">-</span>}
                        </td>
                        <td className="py-3 pr-3 text-zinc-600 text-xs">{c.dateOfBirth || <span className="text-zinc-300">-</span>}</td>
                        <td className="py-3 pr-3 text-center">
                          {c.hasCmnd
                            ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 text-xs font-bold">v</span>
                            : <span className="text-zinc-300 text-xs">-</span>}
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <span className={`text-sm font-semibold ${c.bookingsThisMonth > 0 ? "text-coffee-600" : "text-zinc-300"}`}>
                            {c.bookingsThisMonth}
                          </span>
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <span className={`text-sm font-semibold ${c.bookingsThisYear > 0 ? "text-coffee-600" : "text-zinc-300"}`}>
                            {c.bookingsThisYear}
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <span className="font-bold text-zinc-900 text-sm">{c.bookingsAllTime}</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Room view detail modal ── */}
      {viewDetailRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setViewDetailRoom(null)}>
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-base font-semibold text-zinc-900">{viewDetailRoom.name}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">Thống kê lượt xem</p>
              </div>
              <button onClick={() => setViewDetailRoom(null)} className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors">
                <X size={16} />
              </button>
            </div>

            {viewDetailLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-7 w-7 border-2 border-coffee-600 border-t-transparent" />
              </div>
            ) : viewDetail ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-center">
                    <p className="text-xs text-zinc-400 mb-1.5">Hôm nay</p>
                    <p className="text-3xl font-bold text-coffee-600">{viewDetail.today}</p>
                    <p className="text-xs text-zinc-400 mt-1">lượt xem</p>
                  </div>
                  <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-center">
                    <p className="text-xs text-zinc-400 mb-1.5">Tổng cộng</p>
                    <p className="text-3xl font-bold text-zinc-800">{viewDetail.total.toLocaleString()}</p>
                    <p className="text-xs text-zinc-400 mt-1">lượt xem</p>
                  </div>
                </div>

                <p className="text-xs text-zinc-400 mb-3">30 ngày gần nhất</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={viewDetail.daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#a1a1aa" }} tickFormatter={(v: string) => v.slice(5)} interval={4} />
                    <YAxis tick={{ fontSize: 10, fill: "#a1a1aa" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 8, fontSize: 12 }}
                      formatter={(v: number) => [`${v} lượt`, ""]}
                      labelFormatter={(l: string) => l}
                    />
                    <Line type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : null}
          </div>
        </div>
      )}

    </div>
  );
}

function exportCustomerCSV(data: CustomerStat[], month: string, year: string) {
  const monthLabel = `${month?.split("-")[1]}/${month?.split("-")[0]}`;
  const headers = ["STT","Họ tên","Email","Số điện thoại","Giới tính","Ngày sinh","Có CCCD",`Booking tháng ${monthLabel}`,`Booking năm ${year}`,"Tổng booking"];
  const genderLabel = (g: string) => g === "male" ? "Nam" : g === "female" ? "Nữ" : g === "other" ? "Khác" : "";
  const rows = data.map((c, i) => [
    i + 1, c.name, c.email, c.phone, genderLabel(c.gender), c.dateOfBirth,
    c.hasCmnd ? "Có" : "Không", c.bookingsThisMonth, c.bookingsThisYear, c.bookingsAllTime,
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `khach-hang-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

